/* eslint-disable no-param-reassign */
import { WritableDraft } from 'immer';
import { core_v1_DataSource_FieldSpec } from '@antimatterhq/asl-api-ts-v1';
import { setFiltersText } from '../fieldUtils/filters/datasourceUpgradeFiltersHelper';
import {
  acceptField,
  rejectField,
  setFieldSpecText,
} from '../fieldSpec/datasourceUpgradeFieldSpecHelper';
import {
  DatasourceUpgradeDiff,
  DatasourceUpgradeFieldStatus,
  DatasourceUpgradeItemDiff,
  DatasourceUpgradeTableDiff,
  DatasourceUpgradeMedallionDiff,
  DatasourceUpgradeStatus,
  DatasourceUpgradeFieldSpecDiff,
} from '../datasourceUpgrade.types';
import { filterNulls } from '@/utils/filterNulls';
import {
  Datasource,
  Datasource_Gold_AddTable,
  Datasource_Gold_ModifyTable,
  Datasource_Preset,
  Datasource_Preset_Gold,
} from '@/types/api';

export const getGoldChanges = (
  upgradeDiff: DatasourceUpgradeDiff | undefined,
) => {
  if (!upgradeDiff?.spec.gold) {
    return [];
  }

  return upgradeDiff.spec.gold.map((transform) => {
    const diff = transform.diff;

    // we all only want the table changes if the table has not been added or removed
    if (diff.status === 'unchanged') {
      diff.changes = filterNulls([
        ...(transform.fields || []),
        transform.filter,
        transform.postFilter,
      ]).filter((change) => change.status !== 'unchanged');

      // else if the table has been added or removed, we want to show the diff
    } else if (transform.diff.status !== 'unchanged') {
      diff.changes = [transform.diff];
    }

    return diff;
  });
};

export const setGoldTableText = (tableDiff: DatasourceUpgradeTableDiff) => {
  if (tableDiff.status === 'unchanged') {
    return;
  }

  if (tableDiff.currentPresetValue) {
    tableDiff.title = 'table removed';
    tableDiff.description = `"${tableDiff.tableName}" table removed`;
    tableDiff.content = tableDiff.currentPresetValue?.fields
      ?.map((field) => {
        return field?.name;
      })
      .join(', ');

    return;
  }

  tableDiff.title = 'table added';
  tableDiff.description = `"${tableDiff.tableName}" table added`;
  tableDiff.content =
    'columns: ' +
    tableDiff.upgradePresetValue?.fields
      ?.map((field) => {
        return field?.name;
      })
      .join(', ');
};

export const setGoldText = (upgradeDiff: DatasourceUpgradeDiff | undefined) => {
  if (!upgradeDiff?.spec.gold) {
    return;
  }

  upgradeDiff.spec.gold.forEach((transform) => {
    transform.fields?.forEach((field) => {
      setFieldSpecText(field);
    });
    setFiltersText(transform);
    setGoldTableText(transform.diff);
  });
};

export const queryGoldChanges = (
  gold: DatasourceUpgradeMedallionDiff,
  query: { status?: DatasourceUpgradeFieldStatus } = {},
) => {
  return gold.tables.reduce<Array<DatasourceUpgradeItemDiff>>((acc, table) => {
    return [
      ...acc,
      ...table.changes.filter(
        (change) => !query?.status || change.status === query.status,
      ),
    ];
  }, []);
};

export const getGoldTable = (datasource: Datasource, tableName: string) => {
  const table = datasource.spec.gold?.presetOverrides?.modifyTables?.find(
    (t) => t.name === tableName,
  );

  if (!table) {
    throw new Error(`Unable to find gold table "${tableName}"`);
  }

  return table;
};

export const setGoldFilterChange = ({
  tableName,
  datasource,
  value,
}: {
  tableName: string;
  datasource: WritableDraft<Datasource>;
  value: string | undefined;
}) => {
  const table = getGoldTable(datasource, tableName);

  table.filter = value;
};

export const setGoldPostFilterChange = ({
  tableName,
  datasource,
  value,
}: {
  tableName: string;
  datasource: WritableDraft<Datasource>;
  value: string | undefined;
}) => {
  const table = getGoldTable(datasource, tableName);

  table.postFilter = value;
};

export const setGoldInputChange = ({
  tableName,
  datasource,
  value,
}: {
  tableName: string;
  datasource: WritableDraft<Datasource>;
  value: string;
}) => {
  const table = getGoldTable(datasource, tableName);

  table.sourceTable = value;
};

export const getGoldTableStatus = ({
  datasource,
  upgradePresetValue,
  tableName,
}: {
  datasource: Datasource;
  upgradePresetValue: Datasource_Preset;
  tableName: string;
}): DatasourceUpgradeFieldStatus => {
  const isCurrentlyOmitted =
    datasource.spec.gold?.presetOverrides?.omitTables?.includes(tableName);

  // if the table is omitted we don't care if it's been added or removed
  if (isCurrentlyOmitted) {
    return 'unchanged';
  }

  const existsInUpgradePreset = upgradePresetValue.gold?.some(
    (t) => t.name === tableName,
  );
  const existsInDatasource =
    datasource.spec.gold?.presetOverrides?.modifyTables?.some(
      (t) => t.name === tableName,
    );

  // table added
  if (!existsInDatasource && existsInUpgradePreset) {
    return 'unapplied';
  }

  // table removed
  if (existsInDatasource && !existsInUpgradePreset) {
    return 'unapplied';
  }

  return 'unchanged';
};

export const setGoldTableStatus = ({
  draft,
  status,
  tableName,
}: {
  draft: WritableDraft<DatasourceUpgradeStatus>;
  status: DatasourceUpgradeFieldStatus;
  tableName: string;
}) => {
  const table = draft.spec.gold?.find((t) => t.name === tableName);

  if (!table) {
    throw new Error(`Unable to find gold table "${tableName}"`);
  }

  table.status = status;
};

export const acceptGoldTableChanges = ({
  diff,
  datasource,
}: {
  diff: DatasourceUpgradeTableDiff;
  datasource: WritableDraft<Datasource>;
}) => {
  if (diff.status === 'unchanged') {
    throw new Error(
      `Cannot accept unchanged gold table changes for ${diff.tableName}`,
    );
  }

  const { currentPresetValue, upgradePresetValue } = diff;

  const isTableAdded = !currentPresetValue && upgradePresetValue;

  // if we are adding a table we don't need to do anything
  if (isTableAdded) {
    return;
  }

  if (!datasource.spec.gold?.presetOverrides?.modifyTables) {
    throw new Error(
      `Cannot accept gold table changes for ${diff.tableName} because the table is not in the preset`,
    );
  }

  // we are deleting the table so remove any references to modifyTables
  const { modifyTables } = datasource.spec.gold.presetOverrides;
  const { addTables } = datasource.spec.gold.presetOverrides;

  datasource.spec.gold.presetOverrides.modifyTables = modifyTables.filter(
    (t) => t.name !== diff.tableName,
  );

  // if we are deleting a table then we need to remove it from the addTables as well
  if (addTables) {
    datasource.spec.gold.presetOverrides.addTables = addTables.filter(
      (t) => t.name !== diff.tableName,
    );
  }
};

export const rejectGoldTableChanges = ({
  diff,
  datasource,
}: {
  diff: DatasourceUpgradeTableDiff;
  datasource: WritableDraft<Datasource>;
}) => {
  if (diff.status === 'unchanged') {
    throw new Error(
      `Cannot accept unchanged gold table changes for ${diff.tableName}`,
    );
  }

  if (diff.medallion !== 'gold') {
    throw new Error(
      `Cannot reject gold table changes for ${diff.tableName} because the table is not in the preset`,
    );
  }

  const { currentPresetValue, upgradePresetValue } = diff;

  const isTableAdded = !currentPresetValue && upgradePresetValue;

  if (!datasource.spec.gold?.presetOverrides) {
    throw new Error(
      `Cannot reject gold table changes for ${diff.tableName} because the table is not in the preset`,
    );
  }

  // if we are rejecting an added table then we need to omit it
  if (isTableAdded) {
    datasource.spec.gold.presetOverrides.omitTables = [
      ...(datasource.spec.gold.presetOverrides.omitTables || []),
      diff.tableName,
    ];
    return;
  }

  // else we need to find the modified table and add to add tables

  if (!datasource.spec.gold.presetOverrides.addTables) {
    datasource.spec.gold.presetOverrides.addTables = [];
  }

  const { addTables } = datasource.spec.gold.presetOverrides;

  if (addTables.some((t) => t.name === diff.tableName)) {
    console.warn(
      `Cannot reject gold table changes for ${diff.tableName} because the table is in the addTables preset`,
    );
    return;
  }

  const modifyTable = diff.currentValue;

  if (!modifyTable) {
    throw new Error(
      `Unable to find gold table "${diff.tableName}" for rejecting table removal`,
    );
  }

  // TODO this doesn't work right now. What's happening is the table is being added but
  // the api is still complaining that a duplicate table is being added. It must be comparing
  // to the previous preset version.
  const addTable = {
    name: diff.tableName,
    fields: currentPresetValue?.fields || undefined,
    filter: modifyTable.filter || undefined,
    postFilter: modifyTable.postFilter || undefined,
    custom: modifyTable.custom || undefined,
    sourceTable: modifyTable.sourceTable,
  };

  // we are rejecting the table removal so we need to add it back to the addTables
  addTables.push(addTable);
};

export const getGoldTableDiff = ({
  currentValue,
  upgradeValue,
  currentPresetValue,
  upgradePresetValue,
  status,
  upgradeOmitTables,
  upgradedAddTable,
}: {
  currentValue: Datasource_Gold_ModifyTable | undefined;
  upgradeValue: Datasource_Gold_ModifyTable | undefined;
  currentPresetValue: Datasource_Preset_Gold | undefined;
  upgradePresetValue: Datasource_Preset_Gold | undefined;
  status: DatasourceUpgradeFieldStatus;
  upgradeOmitTables: string[];
  upgradedAddTable: Datasource_Gold_AddTable | undefined;
}): DatasourceUpgradeTableDiff => {
  const tableName = upgradePresetValue?.name || currentValue?.name || '';
  return {
    tableName,
    type: 'table',
    medallion: 'gold',
    changes: [], // changes get added in getGoldChanges
    status,
    currentValue,
    upgradeValue,
    currentPresetValue,
    upgradePresetValue,
    upgradedAddTable,
    upgradeOmitted: upgradeOmitTables.includes(tableName),
    updateStatus: (params: {
      draft: WritableDraft<DatasourceUpgradeStatus>;
      status: DatasourceUpgradeFieldStatus;
    }) =>
      setGoldTableStatus({
        ...params,
        tableName,
      }),
    acceptChanges: (params: {
      diff: DatasourceUpgradeTableDiff;
      datasource: WritableDraft<Datasource>;
    }) =>
      acceptGoldTableChanges({
        ...params,
      }),
    rejectChanges: (params: {
      diff: DatasourceUpgradeTableDiff;
      datasource: WritableDraft<Datasource>;
    }) =>
      rejectGoldTableChanges({
        ...params,
      }),
  };
};

export const rejectGoldField = (
  fieldDiff: DatasourceUpgradeFieldSpecDiff,
  datasource: WritableDraft<Datasource>,
) => {
  const { tableName } = fieldDiff;

  const table = getGoldTable(datasource, tableName!);

  rejectField(
    (fields: string[]) => (table.omitFields = fields),
    (fields: core_v1_DataSource_FieldSpec[]) => (table.addFields = fields),
    table.omitFields || [],
    table.addFields || [],
    fieldDiff,
  );
};

export const acceptGoldField = (
  fieldDiff: DatasourceUpgradeFieldSpecDiff,
  datasource: WritableDraft<Datasource>,
) => {
  const { tableName } = fieldDiff;

  const table = getGoldTable(datasource, tableName!);

  acceptField(
    (fields: string[]) => (table.omitFields = fields),
    (fields: core_v1_DataSource_FieldSpec[]) => (table.addFields = fields),
    table.omitFields || [],
    table.addFields || [],
    fieldDiff,
  );
};
